'use client';
import Typography from '@/components/shared/typography/typography.component';
import classes from './style.module.scss';
import {
    CheckVerifiedWhite,
    Deg360,
    GalleryIcon,
    ImperfectionsIcon,
} from '../../../../../../public/Images';
import { useAppDispatch, useAppSelector, useTranslation } from '@/hook';
import {
    setInnerModalFlag,
    setInnerModalType,
} from '@/redux/reducers/gallery.reducer';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { ModalTypes } from '@/types/redux-state.types';
import Image from 'next/image';
import { CarTypesEnum } from '@/components/car-details-v2/car-type';
const Tooltip = ({
    text,
    content,
    openToolTip,
    setOpenToolTip,
}: {
    text: JSX.Element;
    content: JSX.Element;
    openToolTip: boolean;
    setOpenToolTip: Dispatch<SetStateAction<boolean>>;
}) => {
    const tooltipRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = event => {
            if (
                tooltipRef.current &&
                !tooltipRef.current.contains(event.target)
            ) {
                setOpenToolTip(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    return (
        <div className="relative flex w-full" ref={tooltipRef}>
            <button
                className="py-2 text-white rounded flex-1 flex"
                onClick={() => {
                    if (openToolTip) {
                        setOpenToolTip(!openToolTip);
                    }
                }}
            >
                {content}
            </button>
            {openToolTip && (
                <div className="w-full absolute shadow-lg bg-success-500 text-white font-semibold px-3 py-[6px] text-[13px] right-0 left-0 mx-auto  -bottom-8 rounded before:w-4 before:h-4 before:rotate-45 before:bg-success-500 before:absolute before:z-[-1] before:-top-1 before:left-0  before:right-0 before:mx-auto z-10">
                    {text}
                </div>
            )}
        </div>
    );
};

const GalleryButtons = ({
    fireEvent,
    images,
    carType,
}: {
    fireEvent: () => void;
    images: string[];
    carType: CarTypesEnum;
}) => {
    const { galleryImages, imperfectionsImages } = useAppSelector(
        state => state.gallery
    );
    const [imperfectionsTooltip, setImperfectionsTooltip] = useState(false);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const isDealerCar = carType === CarTypesEnum.Dealer;

    const onButtonClicked = (type: ModalTypes) => {
        fireEvent();
        dispatch(setInnerModalType(type));
        dispatch(setInnerModalFlag(true));
    };

    const TooltipContent = () => {
        return (
            <div className={classes.tooltipContainer}>
                <Image
                    src={CheckVerifiedWhite}
                    width={20}
                    height={20}
                    className={classes.tooltipIcon}
                    alt="CheckVerifiedWhite"
                />
                <Typography variant="captionMedium">
                    {t('There are no imperfections uploaded on this car!')}
                </Typography>
            </div>
        );
    };

    return (
        <div className={classes.buttonsContainer}>
            {images && !isDealerCar ? (
                <button
                    className={classes.button}
                    onClick={() => {
                        onButtonClicked(ModalTypes.View360);
                    }}
                >
                    <Image
                        src={Deg360}
                        alt="Deg360"
                        className="mb-1"
                        width={28}
                        height={28}
                    />{' '}
                    <Typography
                        variant={'body1SemiBold'}
                        className={classes.buttonTitle}
                    >
                        {t('View 360')}
                    </Typography>
                </button>
            ) : null}

            <button
                className={classes.button}
                onClick={() => onButtonClicked(ModalTypes.Gallery)}
            >
                <Image
                    src={GalleryIcon}
                    alt="GalleryIcon"
                    width={13.33}
                    height={13.33}
                />
                <Typography
                    variant="body1SemiBold"
                    className={classes.buttonTitle}
                >
                    {t('Gallery')}
                </Typography>
                <div className={classes.countContainer}>
                    <Typography
                        variant="body1SemiBold"
                        className={classes.buttonTitle}
                    >
                        {galleryImages.length}
                    </Typography>
                </div>
            </button>
            <div className="w-full md:hidden">
                <Tooltip
                    setOpenToolTip={setImperfectionsTooltip}
                    openToolTip={imperfectionsTooltip}
                    content={
                        <div
                            className={classes.button}
                            style={{
                                opacity:
                                    imperfectionsImages.length === 0 ? 0.5 : 1,
                            }}
                            onClick={() =>
                                imperfectionsImages.length === 0
                                    ? setImperfectionsTooltip(true)
                                    : onButtonClicked(ModalTypes.Imperfections)
                            }
                        >
                            <Image
                                src={ImperfectionsIcon}
                                alt="ImperfectionsIcon"
                                width={17.11}
                                height={15}
                            />

                            <Typography
                                variant="body1SemiBold"
                                className={classes.buttonTitle}
                            >
                                {t('Imperfections')}
                            </Typography>
                        </div>
                    }
                    text={<TooltipContent />}
                />
            </div>
            <div className="hidden md:flex flex-1">
                <Tooltip
                    setOpenToolTip={setImperfectionsTooltip}
                    openToolTip={imperfectionsTooltip}
                    content={
                        <div
                            className="hidden md:flex h-[40px] flex-1 border-solid border  rounded-lg border-gray-300 gap-2 justify-center items-center"
                            style={{
                                opacity:
                                    imperfectionsImages.length === 0 ? 0.5 : 1,
                            }}
                            onClick={() =>
                                imperfectionsImages.length === 0
                                    ? setImperfectionsTooltip(true)
                                    : onButtonClicked(ModalTypes.Imperfections)
                            }
                        >
                            <Image
                                src={ImperfectionsIcon}
                                alt="ImperfectionsIcon"
                                width={17.11}
                                height={15}
                            />
                            <Typography
                                variant="body1SemiBold"
                                className={classes.buttonTitle}
                            >
                                {t('Imperfections')}
                            </Typography>
                        </div>
                    }
                    text={<TooltipContent />}
                />
            </div>
        </div>
    );
};

export default GalleryButtons;
